import { useState, useEffect } from 'react';
import { senderDomainAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Plus, Trash2, Loader2, CheckCircle, XCircle, Pause, Play, Shield, Copy, Globe, RefreshCw, Zap,
} from 'lucide-react';
import { toast } from 'sonner';

export default function SenderDomainsPage() {
  const [domains, setDomains] = useState([]);
  const [capacity, setCapacity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [dnsOpen, setDnsOpen] = useState(null);
  const [dnsRecords, setDnsRecords] = useState([]);
  const [form, setForm] = useState({ domain: '', from_email: '', from_name: '', reply_to: '' });

  const load = async () => {
    try {
      const [dRes, cRes] = await Promise.all([senderDomainAPI.list(), senderDomainAPI.capacity()]);
      setDomains(dRes.data);
      setCapacity(cRes.data);
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    if (!form.domain || !form.from_email || !form.from_name) return toast.error('Tum alanlari doldurun');
    try {
      await senderDomainAPI.add(form);
      toast.success('Domain eklendi');
      setAddOpen(false);
      setForm({ domain: '', from_email: '', from_name: '', reply_to: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Eklenemedi'); }
  };

  const handleVerify = async (id) => {
    await senderDomainAPI.verify(id);
    toast.success('Domain dogruland');
    load();
  };

  const handlePause = async (id) => {
    await senderDomainAPI.pause(id);
    toast.success('Domain duraklatildi');
    load();
  };

  const handleResume = async (id) => {
    await senderDomainAPI.resume(id);
    toast.success('Domain aktif');
    load();
  };

  const handleDelete = async (id) => {
    await senderDomainAPI.delete(id);
    toast.success('Domain silindi');
    load();
  };

  const showDNS = async (d) => {
    try {
      const res = await senderDomainAPI.dns(d.id);
      setDnsRecords(res.data.records);
      setDnsOpen(d);
    } catch (e) { toast.error('DNS alinamadi'); }
  };

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Kopyalandi');
  };

  const statusBadge = (s) => {
    const m = { verified: { label: 'Dogrulanmis', variant: 'default' }, pending_dns: { label: 'DNS Bekliyor', variant: 'secondary' }, paused: { label: 'Duraklatildi', variant: 'destructive' }, danger: { label: 'Tehlike', variant: 'destructive' } };
    const c = m[s] || { label: s, variant: 'outline' };
    return <Badge variant={c.variant}>{c.label}</Badge>;
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-6" data-testid="sender-domains">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold">Sender Domains</h1>
          <p className="text-muted-foreground mt-1">Multi-domain email gonderim yonetimi</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => { setLoading(true); load(); }}><RefreshCw className="w-4 h-4 mr-1" /> Yenile</Button>
          <Button size="sm" onClick={() => setAddOpen(true)} data-testid="add-domain-btn"><Plus className="w-4 h-4 mr-1" /> Domain Ekle</Button>
        </div>
      </div>

      {/* Capacity */}
      {capacity && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="rounded-lg bg-card p-4 text-center">
            <Globe className="w-5 h-5 mx-auto text-blue-500 mb-1" />
            <p className="text-2xl font-bold">{capacity.total_domains}</p>
            <p className="text-xs text-muted-foreground">Aktif Domain</p>
          </div>
          <div className="rounded-lg bg-card p-4 text-center">
            <Zap className="w-5 h-5 mx-auto text-amber-500 mb-1" />
            <p className="text-2xl font-bold">{capacity.total_daily_limit?.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground">Gunluk Kapasite</p>
          </div>
          <div className="rounded-lg bg-card p-4 text-center">
            <CheckCircle className="w-5 h-5 mx-auto text-green-500 mb-1" />
            <p className="text-2xl font-bold">{capacity.total_sent_today?.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground">Bugun Gonderildi</p>
          </div>
          <div className="rounded-lg bg-card p-4 text-center">
            <Shield className="w-5 h-5 mx-auto text-cyan-500 mb-1" />
            <p className="text-2xl font-bold">{capacity.total_remaining?.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground">Kalan Kapasite</p>
          </div>
        </div>
      )}

      {/* Domain List */}
      {domains.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center py-16">
            <Globe className="w-12 h-12 text-muted-foreground/30 mb-4" />
            <p className="text-lg font-medium text-muted-foreground">Henuz domain yok</p>
            <Button className="mt-4" onClick={() => setAddOpen(true)}><Plus className="w-4 h-4 mr-1" /> Domain Ekle</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {domains.map(d => (
            <Card key={d.id}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${d.health_score >= 80 ? 'bg-green-500/10' : d.health_score >= 50 ? 'bg-amber-500/10' : 'bg-red-500/10'}`}>
                      <span className={`text-lg font-bold ${d.health_score >= 80 ? 'text-green-500' : d.health_score >= 50 ? 'text-amber-500' : 'text-red-500'}`}>{d.health_score}</span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{d.domain}</p>
                        {statusBadge(d.status)}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{d.from_email} | {d.from_name}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        <span>Day {d.warmup_day}</span>
                        <span>Limit: {d.daily_limit}</span>
                        <span>Bugun: {d.sent_today}</span>
                        <span>Open: %{d.open_rate}</span>
                        <span>Bounce: %{d.bounce_rate}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="outline" size="sm" onClick={() => showDNS(d)}>DNS</Button>
                    {d.status === 'pending_dns' && <Button size="sm" onClick={() => handleVerify(d.id)}><CheckCircle className="w-3 h-3 mr-1" /> Dogrula</Button>}
                    {d.status === 'verified' && <Button variant="outline" size="sm" onClick={() => handlePause(d.id)}><Pause className="w-3 h-3 mr-1" /> Duraklat</Button>}
                    {(d.status === 'paused' || d.status === 'danger') && <Button size="sm" onClick={() => handleResume(d.id)}><Play className="w-3 h-3 mr-1" /> Aktif Et</Button>}
                    <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(d.id)}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>
                <Progress value={d.daily_limit > 0 ? (d.sent_today / d.daily_limit * 100) : 0} className="h-1.5 mt-3" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Domain Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Sender Domain Ekle</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div><Label>Domain</Label><Input placeholder="ornek.com" value={form.domain} onChange={e => setForm({ ...form, domain: e.target.value })} /></div>
            <div><Label>From Email</Label><Input placeholder="noreply@ornek.com" value={form.from_email} onChange={e => setForm({ ...form, from_email: e.target.value })} /></div>
            <div><Label>From Name</Label><Input placeholder="Firma Adi" value={form.from_name} onChange={e => setForm({ ...form, from_name: e.target.value })} /></div>
            <div><Label>Reply-To (opsiyonel)</Label><Input placeholder="support@ornek.com" value={form.reply_to} onChange={e => setForm({ ...form, reply_to: e.target.value })} /></div>
            <Button className="w-full" onClick={handleAdd}><Plus className="w-4 h-4 mr-1" /> Ekle</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* DNS Records Dialog */}
      <Dialog open={!!dnsOpen} onOpenChange={() => setDnsOpen(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>DNS Kayitlari - {dnsOpen?.domain}</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <p className="text-xs text-muted-foreground">Bu CNAME kayitlarini DNS yoneticinize (GoDaddy, Cloudflare vb.) ekleyin:</p>
            {dnsRecords.map((r, i) => (
              <div key={i} className="p-3 rounded-lg bg-muted/30 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">{r.note}</span>
                  <Badge variant="outline" className="text-[10px]">{r.type}</Badge>
                </div>
                <div className="flex items-center gap-2">
                  <code className="text-xs flex-1 truncate">{r.host}</code>
                  <Button variant="ghost" size="sm" onClick={() => copyText(r.host)}><Copy className="w-3 h-3" /></Button>
                </div>
                <div className="flex items-center gap-2">
                  <code className="text-xs flex-1 truncate text-muted-foreground">{r.value}</code>
                  <Button variant="ghost" size="sm" onClick={() => copyText(r.value)}><Copy className="w-3 h-3" /></Button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
